/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { FormFieldPresentationProps } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Select, { SelectComponentProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import { setCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import { xScreen } from "@stoneage/XDL/Utilities";
import * as React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

export interface PaginationPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        currentButton?: StyledProp<PaginationProps>;
        arrowButtons?: StyledProp<PaginationProps>;
        ellipsis?: StyledProp<PaginationProps>;
        linkList?: StyledProp<PaginationProps>;
        pagination?: StyledProp<PaginationProps>;
        perPageSelect?: StyledProp<PaginationProps>;
    };
    /** An object containing props to be passed down to the button component inside pagination.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** The button variant that should be used for the current button.
     * @themable */
    currentPageButtonVariant?: ButtonVariants;
    /** An object containing props to be passed down to the select component inside pagination
     * @themable */
    selectProps?: FormFieldPresentationProps<SelectComponentProps>;
    /** An object containing icon sources for the navigation button icons.
     * @themable */
    navIconsSrc?: {
        firstPage?: React.ComponentType | string;
        previousPage?: React.ComponentType | string;
        nextPage?: React.ComponentType | string;
        lastPage?: React.ComponentType | string;
    };
    bottomPaginationStyle?: InjectableCss;
}

export type PaginationComponentProps = MobiusStyledComponentProps<
    "div",
    {
        pageSizeCookie?: string;
        /** The index of the current page of results being displayed */
        currentPage: number;
        /** String describing the "results per page" select */
        resultsPerPageLabel?: string;
        /** OnChange function passed to the select component governing the number of results per page. Receives the event as an argument. */
        onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => void;
        /** Function to build href passed to each button in the pagination button/link list. Receives the page index as an argument. */
        createHref?: (page: number) => void;
        /** OnChange function passed to each button in the pagination button/link list. Receives the page index as an argument. */
        onChangePage: (page: number) => void;
        /** Number of results currently being displayed on each page */
        resultsPerPage: number;
        /** Number of total results to be displayed within the pagination component */
        resultsCount: number;
        /** Options to be displayed under the "results per page" select */
        resultsPerPageOptions: number[];
        /** Whether the widget is placed on top of bottom of page  */
        isBottom?: boolean;
        /** Whether the page has two pagination items */
        pageHasMultiplePaginations?: boolean;
    }
>;

export type PaginationProps = PaginationPresentationProps & PaginationComponentProps & ThemeProps<BaseTheme>;

const bottomPaginateStyle: any = css`
    justify-content: flex-end;
    padding-top: 13px;
    margin-top: 32px;
    border-top: 1px solid ${getColor("common.ui6")};

    ${xScreen("lte", "xs")} {
        justify-content: center;
    }
`;

const PaginationStyle = styled.div<InjectableCss>`
    display: flex;
    justify-content: flex-end;
    ${injectCss}
`;

const LinkList = styled.nav<InjectableCss>`
    display: flex;
    justify-content: flex-end;
    white-space: nowrap;
    ul {
        display: flex;
        align-items: center;
        list-style: none;
    }
    li {
        &:first-child,
        &:last-child {
            height: 16px;
        }
    }

    ${injectCss}
`;

const PerPageSelect = styled.span<InjectableCss>`
    display: flex;
    align-items: center;
    justify-content: flex-end;

    && select {
        height: 28px;
        width: 58px;
    }

    && select + span {
        width: 14px;
        right: -8px;
    }

    & > div {
        width: 58px;
    }

    ${injectCss}
`;

/**
 * Pagination is an interactive component that provides a navigation interface for paged content.
 */
const Pagination: React.FC<PaginationProps> = withTheme(
    ({ isBottom = false, pageHasMultiplePaginations = false, ...props }) => {
        const {
            createHref,
            currentPage: possibleStringCurrentPage,
            onChangeResultsPerPage,
            onChangePage,
            resultsPerPage: possibleStringResultsPerPage,
            resultsPerPageLabel,
            resultsCount,
            resultsPerPageOptions,
            pageSizeCookie,
            ...otherProps
        } = props;

        const currentPage = convertToNumberIfString(possibleStringCurrentPage);
        const resultsPerPage = convertToNumberIfString(possibleStringResultsPerPage);

        const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
            const value = event.currentTarget.value;
            if (pageSizeCookie) {
                setCookie(pageSizeCookie, value);
            }

            onChangeResultsPerPage(event, parseInt(value, 10));
            event.currentTarget.blur();
        };

        const { spreadProps, applyProp } = applyPropBuilder(otherProps, { component: "pagination" });
        const cssOverrides = spreadProps("cssOverrides" as any);
        const buttonProps = spreadProps("buttonProps" as any);
        const translate = otherProps.theme.translate;

        const finalPageIndex = Math.floor((resultsCount - 1) / resultsPerPage) + 1;
        let pagesToDisplay: ("e" | number)[] = [];
        if (finalPageIndex < 6) {
            for (let i = 1; i <= finalPageIndex; i += 1) {
                pagesToDisplay.push(i);
            }
        } else if (currentPage < 4) {
            pagesToDisplay = [1, 2, 3, 4, "e", finalPageIndex];
        } else if (currentPage >= 4 && currentPage < finalPageIndex - 2) {
            pagesToDisplay = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, "e", finalPageIndex];
        } else {
            pagesToDisplay = [
                "e",
                finalPageIndex - 4,
                finalPageIndex - 3,
                finalPageIndex - 2,
                finalPageIndex - 1,
                finalPageIndex,
            ];
        }

        const paginationButtons = (
            <>
                <li>
                    <TextField
                        css={css`
                            &&& {
                                width: 45px;
                                height: 26px;
                                margin-right: 8px;
                                border-radius: 2px;
                                padding-left: 17px !important;
                                -moz-appearance: textfield;
                            }
                            ::-webkit-outer-spin-button,
                            ::-webkit-inner-spin-button {
                                -webkit-appearance: none;
                                margin: 0;
                            }
                        `}
                        className="paginate"
                        type="number"
                        value={currentPage}
                        onChange={e => {
                            const typedPageIdx = +e.target.value;
                            if (typedPageIdx > finalPageIndex) {
                                onChangePage(finalPageIndex);
                            } else {
                                onChangePage(typedPageIdx);
                            }
                        }}
                        aria-label="pagination-page"
                    />
                </li>
                <li>
                    <Typography size={12} lineHeight="18px" color="text.main">
                        of {finalPageIndex}
                    </Typography>
                </li>
            </>
        );

        const forwardDisabled = currentPage === finalPageIndex || finalPageIndex === 1;
        const backDisabled = currentPage === 1 || finalPageIndex === 1;

        const navButton = (
            pageIndex: number,
            buttonLabel: string,
            disabled: boolean,
            dataTestSelector?: string,
            prev?: boolean,
        ) => (
            <Link
                {...buttonProps}
                css={css`
                    ${buttonProps.css}
                    padding: 0 10px;
                    ${!prev && "padding-right: 0;"}
                    height: 16px;
                    &,
                    > span {
                        line-height: 8px;
                    }

                    && :hover {
                        background-color: unset;
                    }
                `}
                aria-label={buttonLabel}
                typographyProps={{
                    ...buttonProps.typographyProps,
                }}
                onClick={() => onChangePage(pageIndex)}
                disabled={disabled}
                {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
            >
                <Icon
                    src="ChevronDown"
                    color={disabled ? "common.ui6" : "inherit"}
                    size={20}
                    css={css`
                        svg {
                            transform: rotate(${prev ? 90 : -90}deg);
                        }
                    `}
                />
            </Link>
        );

        const perPageSelect = () => {
            return (
                resultsPerPageOptions?.length > 0 && (
                    <PerPageSelect css={cssOverrides.perPageSelect}>
                        <Typography
                            size={12}
                            lineHeight="18px"
                            css={css`
                                margin-right: 8px;
                                white-space: nowrap;
                            `}
                            className="page-size__show"
                        >
                            {translate("Per Page")}
                        </Typography>
                        <Select
                            onChange={changeResultsPerPage}
                            hasLabel={false}
                            preventEllipsis={true}
                            value={resultsPerPage}
                            {...spreadProps("selectProps" as any)}
                            data-test-selector="pagination_resultsPerPage"
                            sizeVariant="small"
                            iconProps={{
                                size: 8,
                                src: "IconArrowDropdownSmall",
                            }}
                            aria-label="pagination-per-page-select"
                        >
                            {resultsPerPageOptions.map((n: string) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </Select>
                    </PerPageSelect>
                )
            );
        };

        return (
            <PaginationStyle
                css={css`
                    ${((pageHasMultiplePaginations && isBottom) || (!pageHasMultiplePaginations && !isBottom)) &&
                    bottomPaginateStyle}
                    ${cssOverrides.pagination}
                `}
                {...omitSingle(otherProps, "cssOverrides")}
            >
                {!isBottom && perPageSelect()}
                <StyledWrapper>
                    <LinkList
                        role="navigation"
                        aria-label={translate("Pagination navigation")}
                        css={css`
                            width: 100%;
                            & {
                                @media (max-width: 481px) {
                                    display: ${!pageHasMultiplePaginations || isBottom ? "flex" : "none"};
                                }
                            }
                            ul {
                                margin-left: 22px;
                            }
                            ${cssOverrides.linkList}
                        `}
                        data-test-selector={`pagination_currentPage_${currentPage}`}
                    >
                        <ul>
                            <li className="prev-button">
                                {navButton(
                                    currentPage - 1,
                                    translate("Prev"),
                                    backDisabled,
                                    "paginationButtonPrevious",
                                    true,
                                )}
                            </li>
                            {paginationButtons}
                            <li className="next-button">
                                {navButton(
                                    currentPage + 1,
                                    translate("Next"),
                                    forwardDisabled,
                                    "paginationButtonNext",
                                    false,
                                )}
                            </li>
                        </ul>
                    </LinkList>
                </StyledWrapper>
            </PaginationStyle>
        );
    },
);

/** @component */
export default Pagination as React.ComponentType<PaginationComponentProps>;

export { Pagination, PaginationStyle };
